import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { HealthResponse, HealthState } from '@elutide/shared';
import { api } from '@/lib/api';
import { qk } from '@/lib/keys';
import { AgeStamp, Skeleton, ErrorState } from '@/components';
import { buildChips, notConfiguredLabels, summarizeChips } from './healthChips';

const TONE: Record<HealthState, string> = {
  ok: '',
  warn: 'o-health--warn',
  crit: 'o-health--crit',
  unknown: 'o-health--unknown',
  not_configured: 'o-health--unknown',
};

/**
 * The band that answers the morning question. One summary pill, then exactly eight chips,
 * each a group of underlying checks where the worst state wins, each with its own age.
 *
 * Checks with nothing wired up never become a chip state, never count as an issue and are
 * never aged. They get one muted line at the bottom saying what is not configured.
 */
export function HealthStrip() {
  const query = useQuery({
    queryKey: qk.healthDetail(),
    queryFn: () => api.get<HealthResponse>('/health/detail'),
    refetchInterval: 60_000,
    staleTime: 60_000,
  });

  if (query.isLoading) {
    return <Skeleton shape="card" lines={1} label="Loading health" />;
  }
  if (query.isError || !query.data) {
    return (
      <div className="o-health o-health--unknown">
        <div className="o-health__summary">
          <span className="o-health__pill">Health unknown</span>
          <span className="o-muted">The health endpoint did not answer.</span>
        </div>
        <ErrorState message="Health data could not be read." onRetry={() => void query.refetch()} />
      </div>
    );
  }

  const data = query.data;
  const chips = buildChips(data.checks);
  const summary = summarizeChips(chips);
  const crit = chips.filter((chip) => chip.state === 'crit').length;
  const warn = chips.filter((chip) => chip.state === 'warn').length;
  const notConfigured = notConfiguredLabels(data.checks);

  return (
    <section className={`o-health ${TONE[summary.state]}`.trim()} aria-label="System health">
      <div className="o-health__summary">
        <span className="o-health__pill">{summary.label}</span>
        <span className="o-muted">
          {crit} critical, {warn} warning, refreshed every 60s
        </span>
        <AgeStamp asOf={data.as_of} klass="live" />
      </div>

      <div className="o-health__chips">
        {chips.map((chip) => (
          <Link key={chip.id} to="/settings/system" hash={chip.id} className="o-health__chip">
            <span className="o-health__chip-name">
              <span
                className={`o-health__dot${chip.state === 'ok' ? '' : ` o-health__dot--${chip.state}`}`}
                aria-hidden="true"
              />
              {chip.label}
            </span>
            <span className="o-health__value">
              {chip.value ?? (chip.state === 'not_configured' ? 'not configured' : chip.state)}
            </span>
            {chip.asOf === null ? null : <AgeStamp asOf={chip.asOf} klass="live" />}
            <span className="o-visually-hidden">state {chip.state}</span>
          </Link>
        ))}
      </div>

      {notConfigured.length === 0 ? null : (
        <p className="o-health__footnote o-muted">Not configured: {notConfigured.join(', ')}</p>
      )}
    </section>
  );
}
